import { useEffect, useMemo, useState } from "react";
import api from "../api/axios";
import { Copy, KeyRound, Loader2, ShieldCheck, Trash2, X } from "lucide-react";

const MCP_API_BASE_URL = (process.env.REACT_APP_API_BASE_URL || "").trim();
const MCP_PUBLIC_URL = MCP_API_BASE_URL
  ? `${MCP_API_BASE_URL.replace(/\/+$/, "")}/mcp`
  : "/mcp";

const emptyForm = {
  name: "",
  description: "",
};

const Modal = ({ title, children, onClose, size = "md" }) => {
  const sizeClass = {
    sm: "w-[420px]",
    md: "w-[620px]",
    lg: "w-[760px]",
  }[size];

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-[2000] p-4">
      <div className={`bg-white rounded-2xl shadow-xl p-6 ${sizeClass} max-h-[90vh] overflow-auto`}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-bold text-gray-900">{title}</h2>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-900">
            <X size={20} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
};

export default function AdminMcpPage() {
  const [tokens, setTokens] = useState([]);
  const [serverInfo, setServerInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [createdToken, setCreatedToken] = useState(null);
  const [revokeTarget, setRevokeTarget] = useState(null);
  const [form, setForm] = useState(emptyForm);

  const activeTokens = useMemo(() => tokens.filter((token) => token.is_active), [tokens]);

  const fetchData = async () => {
    setLoading(true);
    setError("");
    try {
      const [tokensRes, infoRes] = await Promise.all([
        api.get("/api/admin/mcp/"),
        api.get("/api/admin/mcp/info"),
      ]);
      setTokens(tokensRes.data || []);
      setServerInfo(infoRes.data || null);
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to load MCP configuration");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const handleCreate = async () => {
    if (!form.name.trim()) {
      setError("Token name is required");
      return;
    }

    setSaving(true);
    setError("");
    try {
      const response = await api.post("/api/admin/mcp/", {
        name: form.name.trim(),
        description: form.description.trim() || null,
      });
      setCreatedToken(response.data);
      setCreateOpen(false);
      setForm(emptyForm);
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to create MCP token");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokeTarget) return;
    setSaving(true);
    setError("");
    try {
      await api.post(`/api/admin/mcp/${revokeTarget.id}/revoke`);
      setRevokeTarget(null);
      await fetchData();
    } catch (err) {
      setError(err?.response?.data?.detail || "Failed to revoke MCP token");
    } finally {
      setSaving(false);
    }
  };

  const copyText = async (value) => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      setError("Clipboard copy failed. Copy manually.");
    }
  };

  return (
    <div className="p-8">
      <div className="flex items-start justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">MCP Access</h1>
          <p className="text-sm text-gray-600 mt-1">
            Manage admin-issued read-only MCP tokens and share them with approved AI clients.
          </p>
        </div>
        <button
          onClick={() => setCreateOpen(true)}
          className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg shadow"
        >
          <KeyRound size={18} />
          Create MCP Token
        </button>
      </div>

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 xl:grid-cols-[1.2fr_0.8fr] gap-6 mb-6">
        <section className="bg-white rounded-2xl shadow p-6">
          <div className="flex items-center gap-3 mb-4">
            <ShieldCheck size={20} className="text-blue-600" />
            <h2 className="text-lg font-semibold text-gray-900">What This MCP Server Can Do</h2>
          </div>

          <div className="space-y-3 text-sm text-gray-700">
            {(serverInfo?.features || []).map((feature) => (
              <div key={feature} className="rounded-xl border border-gray-200 bg-gray-50 px-4 py-3">
                {feature}
              </div>
            ))}
          </div>

          <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <div className="rounded-xl border border-blue-100 bg-blue-50 p-4">
              <div className="font-semibold text-blue-900 mb-1">Endpoint</div>
              <div className="text-blue-800 break-all">{MCP_PUBLIC_URL}</div>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50 p-4">
              <div className="font-semibold text-emerald-900 mb-1">Authentication</div>
              <div className="text-emerald-800">{serverInfo?.auth_header || "Authorization: Bearer <token>"}</div>
            </div>
          </div>

          <div className="mt-5">
            <div className="font-semibold text-gray-900 mb-2">Read-only tools</div>
            <div className="flex flex-wrap gap-2">
              {(serverInfo?.tools || []).map((tool) => (
                <span key={tool} className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700 border border-slate-200">
                  {tool}
                </span>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-white rounded-2xl shadow p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Usage</h2>
          <div className="space-y-4 text-sm text-gray-700">
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-semibold text-gray-900 mb-2">Server URL</div>
              <div className="font-mono text-xs bg-gray-50 rounded-lg p-3 overflow-auto mb-3">
                {MCP_PUBLIC_URL}
              </div>
              <div className="font-semibold text-gray-900 mb-2">Headers</div>
              <div className="font-mono text-xs bg-gray-50 rounded-lg p-3 overflow-auto">
                Authorization: Bearer YOUR_MCP_TOKEN{"\n"}
                x-mcp-client-name: your-app-name
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-4">
              <div className="font-semibold text-gray-900 mb-2">Connection notes</div>
              <ul className="list-disc pl-5 space-y-1">
                <li>Only admin-created active tokens are accepted.</li>
                <li>Tools are read-only and cannot modify deals, companies, or users.</li>
                <li>Usage is tracked by token, tool, last client name, and creator.</li>
              </ul>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50 p-4 text-amber-900">
              Use a unique token per consuming application so revocation and audit trails stay clean.
            </div>
          </div>
        </section>
      </div>

      <section className="bg-white rounded-2xl shadow overflow-hidden">
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gray-50">
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Issued MCP Tokens</h2>
            <p className="text-sm text-gray-600">Active: {activeTokens.length} of {tokens.length}</p>
          </div>
        </div>

        <div className="overflow-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-700 border-b">
              <tr>
                <th className="text-left px-5 py-3">Token</th>
                <th className="text-left px-5 py-3">Created By</th>
                <th className="text-left px-5 py-3">How Created</th>
                <th className="text-left px-5 py-3">Last Tool</th>
                <th className="text-left px-5 py-3">Usage</th>
                <th className="text-left px-5 py-3">Status</th>
                <th className="text-right px-5 py-3">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y">
              {loading ? (
                <tr>
                  <td className="px-5 py-10 text-center" colSpan={7}>
                    <div className="flex flex-col items-center gap-2 text-gray-500">
                      <Loader2 size={28} className="animate-spin" />
                      Loading MCP tokens...
                    </div>
                  </td>
                </tr>
              ) : tokens.length === 0 ? (
                <tr>
                  <td className="px-5 py-8 text-gray-500" colSpan={7}>
                    No MCP tokens created yet.
                  </td>
                </tr>
              ) : (
                tokens.map((token) => (
                  <tr key={token.id} className="hover:bg-gray-50 align-top">
                    <td className="px-5 py-4">
                      <div className="font-semibold text-gray-900">{token.name}</div>
                      <div className="text-xs text-gray-500 mt-1">{token.token_preview}</div>
                      {token.description ? <div className="text-xs text-gray-500 mt-2 max-w-xs">{token.description}</div> : null}
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      <div>{token.created_by_name}</div>
                      <div className="text-xs text-gray-500 mt-1">{new Date(token.created_at).toLocaleString()}</div>
                    </td>
                    <td className="px-5 py-4 text-gray-700">{token.creation_source || "admin_ui"}</td>
                    <td className="px-5 py-4 text-gray-700">
                      <div>{token.last_used_tool || "—"}</div>
                      <div className="text-xs text-gray-500 mt-1">{token.last_used_client || "No client recorded"}</div>
                    </td>
                    <td className="px-5 py-4 text-gray-700">
                      <div>{token.usage_count || 0} calls</div>
                      <div className="text-xs text-gray-500 mt-1">
                        {Object.keys(token.tool_usage_counts || {}).length > 0
                          ? Object.entries(token.tool_usage_counts)
                              .slice(0, 3)
                              .map(([toolName, count]) => `${toolName}: ${count}`)
                              .join(", ")
                          : "No tool usage yet"}
                      </div>
                    </td>
                    <td className="px-5 py-4">
                      <span
                        className={`inline-flex px-3 py-1 rounded-full text-xs font-semibold border ${
                          token.is_active
                            ? "bg-green-50 border-green-200 text-green-700"
                            : "bg-gray-100 border-gray-200 text-gray-600"
                        }`}
                      >
                        {token.is_active ? "Active" : "Revoked"}
                      </span>
                    </td>
                    <td className="px-5 py-4">
                      <div className="flex justify-end gap-2">
                        {token.is_active ? (
                          <button
                            onClick={() => setRevokeTarget(token)}
                            className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-red-200 text-red-700 hover:bg-red-50"
                          >
                            <Trash2 size={16} />
                            Revoke
                          </button>
                        ) : (
                          <span className="text-xs text-gray-400 self-center">Inactive</span>
                        )}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      {createOpen && (
        <Modal title="Create MCP Token" onClose={() => setCreateOpen(false)}>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Token name</label>
              <input
                type="text"
                value={form.name}
                onChange={(e) => setForm((current) => ({ ...current, name: e.target.value }))}
                placeholder="e.g. Claude Desktop - Analytics Team"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((current) => ({ ...current, description: e.target.value }))}
                placeholder="Optional note about who or what will use this token"
                rows={4}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              />
            </div>
            <div className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              The full token value is only shown once after creation. Save it in the MCP client immediately.
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <button onClick={() => setCreateOpen(false)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleCreate}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
              >
                {saving ? "Creating..." : "Create Token"}
              </button>
            </div>
          </div>
        </Modal>
      )}

      {createdToken && (
        <Modal title="MCP Token Created" onClose={() => setCreatedToken(null)} size="lg">
          <div className="space-y-4">
            <div className="rounded-xl border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900">
              Token created successfully. This plain token will not be shown again.
            </div>
            <div>
              <div className="text-sm font-medium text-gray-700 mb-2">Plain token</div>
              <div className="flex items-center gap-2">
                <textarea
                  readOnly
                  value={createdToken.plain_token}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
                />
                <button
                  onClick={() => copyText(createdToken.plain_token)}
                  className="shrink-0 inline-flex items-center gap-2 px-3 py-2 rounded-lg border hover:bg-gray-50"
                >
                  <Copy size={16} />
                  Copy
                </button>
              </div>
            </div>
            <div className="rounded-xl border border-gray-200 p-4 text-sm text-gray-700">
              Use this token in MCP clients as the bearer token for the embedded backend MCP endpoint.
            </div>
            <div className="flex justify-end">
              <button onClick={() => setCreatedToken(null)} className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">
                Close
              </button>
            </div>
          </div>
        </Modal>
      )}

      {revokeTarget && (
        <Modal title="Revoke MCP Token" onClose={() => setRevokeTarget(null)} size="sm">
          <div className="space-y-4">
            <p className="text-sm text-gray-700">
              Revoke <span className="font-semibold">{revokeTarget.name}</span>? Connected MCP clients using this token will stop working immediately.
            </p>
            <div className="flex justify-end gap-3">
              <button onClick={() => setRevokeTarget(null)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
                Cancel
              </button>
              <button
                onClick={handleRevoke}
                disabled={saving}
                className="px-4 py-2 rounded-lg bg-red-600 text-white hover:bg-red-700 disabled:opacity-60"
              >
                {saving ? "Revoking..." : "Revoke Token"}
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
